import { useState, type JSX, type ReactNode } from "react";

/**
 * P2d builder presentational components (design spec §03/§06). CDS-faithful
 * per the fidelity table (§00): step-card / contract / property-row / evidence
 * vocabulary mirrors ui_kits/cycle-app agents-04/04b/05 + connectors-modal.
 * All components are CONTROLLED — state and API calls stay in ProfilesSection
 * (the P2a pipeline: draft, proposals, publish, preview is reused unchanged).
 */

/** One builder-chat exchange (client-held; never persisted — spec §03 rule 4). */
export interface ChatTurn {
  role: "admin" | "assistant";
  text: string;
}

/** Left pane: the real conversation (multi-round; thinking line while busy). */
export function BuilderChat(props: {
  turns: ChatTurn[];
  busy: boolean;
  input: string;
  onInput: (v: string) => void;
  onSend: () => void;
  errors: string[];
  /** Quick actions under the composer (agents-05 left column pattern). */
  actions: { label: string; onClick: () => void }[];
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="bd-chat" data-testid="profiles-assist">
      <div className="bd-chat-head">想怎么改这个助手？</div>
      <div className="bd-chat-log">
        {props.turns.length === 0 && (
          <p className="bd-chat-empty">
            用一句话说清想改什么，比如「别让它承诺处理时限」；改法会标在右侧，逐条采纳。
          </p>
        )}
        {props.turns.map((t, i) => (
          <div key={i} className={t.role === "admin" ? "bd-msg-user" : "bd-msg-a"}>
            {t.text}
          </div>
        ))}
        {props.busy && <div className="bd-thinking">正在核对当前配置与边界</div>}
        {props.errors.map((m) => (
          <p key={m} className="bd-error">
            {m}
          </p>
        ))}
      </div>
      {props.children}
      <div className="bd-composer">
        <textarea
          rows={2}
          value={props.input}
          placeholder="继续说，或直接点右侧条目修改"
          onChange={(e) => props.onInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              props.onSend();
            }
          }}
        />
        <button disabled={props.busy || props.input.trim().length === 0} onClick={props.onSend}>
          发送
        </button>
      </div>
      <div className="bd-quick">
        {props.actions.map((a) => (
          <button key={a.label} className="bd-chip" onClick={a.onClick}>
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The WILL / WON'T contract card (agents-04 pattern). Chips ride line ends. */
export function ContractCard(props: {
  will: string[];
  wont: { text: string; chip?: ReactNode }[];
}): JSX.Element {
  return (
    <div className="bd-contract" data-testid="profiles-contract">
      <div className="bd-contract-half bd-will">
        <div className="bd-contract-hd">WILL · 会做</div>
        <ul>
          {props.will.map((w) => (
            <li key={w}>{w}</li>
          ))}
          {props.will.length === 0 && <li className="bd-dim">还没有写它会做什么</li>}
        </ul>
      </div>
      <div className="bd-contract-half bd-wont">
        <div className="bd-contract-hd">WON'T · 不做</div>
        <ul>
          {props.wont.map((w, i) => (
            <li key={`${w.text}-${i}`}>
              {w.text}
              {w.chip}
            </li>
          ))}
          {props.wont.length === 0 && <li className="bd-dim">还没有边界条目</li>}
        </ul>
      </div>
    </div>
  );
}

/** One key-value-evidence row of the living document; whole row drills down. */
export function PropertyRow(props: {
  label: string;
  value: ReactNode;
  evidence?: ReactNode;
  state?: "ok" | "warn" | "off";
  onOpen?: () => void;
  /** Inline expansion (evidence card) rendered under the row when open. */
  expanded?: ReactNode;
  testid?: string;
}): JSX.Element {
  return (
    <div className="bd-rowwrap" {...(props.testid !== undefined ? { "data-testid": props.testid } : {})}>
      <div
        className={`bd-row${props.onOpen !== undefined ? " bd-row-click" : ""}`}
        onClick={props.onOpen}
        role={props.onOpen !== undefined ? "button" : undefined}
      >
        <span className="bd-row-k">{props.label}</span>
        <span className="bd-row-v">
          {props.state !== undefined && <span className={`bd-dot bd-dot-${props.state}`} />}
          {props.value}
        </span>
        {props.evidence !== undefined && <span className="bd-row-ev">{props.evidence}</span>}
      </div>
      {props.expanded}
    </div>
  );
}

/** connectors-modal style key-value evidence lines (inline expansion body). */
export function EvidenceLines(props: {
  lines: { k: string; v: ReactNode }[];
}): JSX.Element {
  return (
    <div className="bd-evcard">
      {props.lines.map((l) => (
        <div key={l.k} className="bd-evrow">
          <span className="bd-ev-k">{l.k}</span>
          <span className="bd-ev-v">{l.v}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Field drill-down drawer (spec §10 default #2): the ORIGINAL form control
 * for one field, save-to-draft / cancel. Rendered inline under the document
 * (the console has no overlay layer; the sliding panel is a P2d-3 polish).
 */
export function DrillDrawer(props: {
  title: string;
  hint?: string;
  onSave: () => void;
  onCancel: () => void;
  onRewrite?: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="bd-drawer" data-testid="profiles-drawer">
      <div className="bd-drawer-head">
        <span className="bd-drawer-title">{props.title}</span>
        {props.hint !== undefined && <span className="bd-drawer-hint">{props.hint}</span>}
      </div>
      {props.children}
      <div className="bd-drawer-actions">
        {props.onRewrite !== undefined && (
          <button className="bd-chip" onClick={props.onRewrite}>
            AI 帮改这一项
          </button>
        )}
        <span className="bd-drawer-spacer" />
        <button onClick={props.onCancel}>取消</button>
        <button className="primary" onClick={props.onSave}>
          存入草稿
        </button>
      </div>
    </div>
  );
}

/** Hook-less inline expander helper for connector evidence rows. */
export function useExpanded(): { open: string | null; toggle: (k: string) => void } {
  const [open, setOpen] = useState<string | null>(null);
  return { open, toggle: (k: string) => setOpen((cur) => (cur === k ? null : k)) };
}

/**
 * Generative step card in the create conversation (agents-02 pattern, spec
 * §02): mono step label + display title, content while active; a confirmed
 * card collapses to one green fact line with a re-open affordance; a future
 * card renders dim (dashed, no content).
 */
export function StepCard(props: {
  step: number;
  total: number;
  title: string;
  hint?: string;
  phase: "done" | "active" | "next";
  /** The collapsed one-line fact once confirmed. */
  fact?: string;
  onReopen?: () => void;
  /** The green footer line while active (live summary of the selection). */
  doneLine?: string;
  children?: ReactNode;
}): JSX.Element {
  if (props.phase === "done") {
    return (
      <div className="bd-step bd-step-done" data-testid={`bd-step-${props.step}`}>
        <span className="bd-step-fact">{props.fact ?? props.title}</span>
        {props.onReopen !== undefined && (
          <button className="bd-chip" onClick={props.onReopen}>
            改
          </button>
        )}
      </div>
    );
  }
  if (props.phase === "next") {
    return (
      <div className="bd-step bd-step-dim" data-testid={`bd-step-${props.step}`}>
        <div className="bd-step-lbl">
          STEP {props.step} / {props.total} · 接下来
        </div>
        <div className="bd-step-title">{props.title}</div>
      </div>
    );
  }
  return (
    <div className="bd-step" data-testid={`bd-step-${props.step}`}>
      <div className="bd-step-lbl">
        STEP {props.step} / {props.total}
      </div>
      <div className="bd-step-title">{props.title}</div>
      {props.hint !== undefined && <div className="bd-step-hint">{props.hint}</div>}
      {props.children}
      {props.doneLine !== undefined && <div className="bd-step-foot">✓ {props.doneLine}</div>}
    </div>
  );
}
